"use client";

import NoReviewIcon from "@/Components/Common/Icons/NoReviewIcon";
import ReviewsDisclaimer from "@/Components/DoctorsDashboard/SearchModule/ReviewsDisclaimer";
import Image from "next/image";
import { useRouter, useSearchParams, usePathname, useParams } from "next/navigation";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { closeReviewPopup, setReviewPopupOpen } from "@/Redux/slice/reviewPopupSlice";
import { toast } from "react-toastify";
import LoginPopup from "./LoginPopup";
import dayjs from "dayjs";
import Star from "@/Components/website/common/Icons/Star";
import RatingStar from "@/Components/Common/Icons/RatingStar";
import OutlineStar from "@/Components/Common/Icons/OutlineStar";
import ReviewCard from "./ReviewCard";
import { FiCheck, FiInfo } from "react-icons/fi";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/Components/ui/tooltip";

// Import extracted components and utilities
import { RatingCircle, ConfidenceCircle, ConfidenceLevelIndicator, AccessibilityCircle, AccessibilityLevelIndicator, staticData, ratingCategories, peerAverages, peerAccessibilityAvg, peerConfidenceAvg, getPeerComparisonStatus, getPeerComparisonStatusPercent, getRatingColor } from "./reviewListingComponents";
import { CLINICAL_CONCERN } from "./components/utils";
import { getAccessibilityLevel } from "./reviewListingComponents/AccessibilityComponents";
import { getConfidenceLevel } from "./reviewListingComponents/ConfidenceComponents";
import { MetricDisplayCard } from "./components/MetricDisplayCard";
import { ThumbsUp, AlertTriangle, LockKeyholeOpen, BicepsFlexed } from "lucide-react";
import { refreshPipelineReviews, refreshProductReviews } from "@/app/actions/reviewCache";
const ReviewListing = ({
  children,
  showMore,
  isLoggedIn,
  isVerified,
  product,
  reviewData,
  productName,
  productImage,
  genericName
}) => {
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  // The same ReviewListing component is mounted on both the FDA
  // product detail page and the pipeline drug detail page. We pick
  // the create endpoint based on URL prefix.
  const isPipelineReview = (pathname || "").startsWith("/pipeline/");
  const user = useSelector(state => state?.userData?.userData?.user);
  const openReviewPopup = useSelector(state_0 => state_0?.reviewPopup?.isOpen);
  // Use Redux user state as fallback for isLoggedIn (covers cases where server prop may be incorrect)
  const isUserLoggedIn = isLoggedIn || !!user;
  const [isHidden, setIsHidden] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isRatingsVisible, setIsRatingsVisible] = useState(false);
  const [isAssessmentsExpanded, setIsAssessmentsExpanded] = useState(false);
  const ratingsSectionRef = useRef(null);
  const confidenceRef = useRef(null);
  const accessibilityRef = useRef(null);
  const params = useParams();
  // Review data the user is guaranteed to see. `reviewData` arrives from
  // the server-rendered page, which is cached for days. Right after a
  // submit this overrides the prop with the review just saved, so the card
  // is on screen before any cache work happens; it is dropped again once
  // the server-rendered prop provably contains that review (see below).
  const [freshReviewData, setFreshReviewData] = useState(null);
  // `_id` of the review the API last confirmed, so the hand-off from the
  // optimistic copy to server data only happens on data that includes it.
  const savedReviewIdRef = useRef(null);

  // Hand back to the server-rendered list as soon as it carries the saved
  // review (the refresh that `refreshProductReviews` triggers). A prop that
  // still lacks it — a stale render — is ignored, so the review can never
  // disappear once shown.
  useEffect(() => {
    const savedId = savedReviewIdRef.current;
    if (!savedId || !freshReviewData) return;
    const serverHasIt = (reviewData?.reviewsList || []).some(r => String(r?._id) === String(savedId));
    if (serverHasIt) setFreshReviewData(null);
  }, [reviewData, freshReviewData]);

  // Auto-open review popup if openReview=true in URL
  useEffect(() => {
    const shouldOpenReview = searchParams.get("openReview");
    if (shouldOpenReview === "true" && isUserLoggedIn && hasPermissionToAddReview(user?.type)) {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        const reviewsSection = document.getElementById('reviews');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
        dispatch(setReviewPopupOpen(true));
      }, 500);
    }
  }, [searchParams, dispatch, isUserLoggedIn, user?.type]);
  const {
    reviewsList: reviews = [],
    globalAverage: globalAvg = 0,
    globalAccessibility = 0,
    globalConfidenceScore = 0,
    globalEfficacy = 0,
    globalSafety = 0,
    globalTolerability = 0,
    globalAdherence = 0,
    globalPriceAndAffordability = 0
  } = (freshReviewData ?? reviewData) || {};
  function validateRatingsAndReview(ratings, review) {
    const hasMissingRating = ratings.some(rating => rating.count === 0);
    return !hasMissingRating;
  }
  const router = useRouter();
  const hasPermissionToAddReview = userType => {
    return isUserLoggedIn && ["physician", "clinic"].includes(user?.type);
  };

  // The API's `individualAverage` is the mean of these five ratings; the
  // optimistic card mirrors that so its star value matches what the server
  // will show once the real record replaces it.
  const RATING_FIELDS = ["efficacy", "safety", "tolerability", "adherence", "priceandaffordability"];
  const mean = values => {
    const nums = values.map(Number).filter(n => Number.isFinite(n));
    return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
  };

  // Shape the just-submitted payload like an entry from the API's
  // enriched `reviewsList`, so ReviewCard can render it unchanged.
  const buildOptimisticReview = (payload, saved) => ({
    ...payload,
    _id: saved?._id || `pending-${Date.now()}`,
    createdAt: saved?.createdAt || new Date().toISOString(),
    individualAverage: mean(RATING_FIELDS.map(f => payload[f])),
    user: {
      _id: user?._id
    },
    physicianDetails: {
      speciality: user?.speciality || user?.profileData?.speciality || "",
      region: user?.region || user?.addressDetails?.state || "",
      experience: user?.experience
    }
  });
  const withReviewPrepended = (base, review_0) => {
    const reviewsList = [review_0, ...(base?.reviewsList || [])];
    return {
      ...(base || {}),
      reviewsList,
      globalAverage: mean(reviewsList.map(r_0 => r_0?.individualAverage))
    };
  };

  // Read the review list back from the API. This hits Mongo directly and is
  // not behind Next's page cache, so it cannot race the background purge —
  // it is the deterministic source of truth after a submit.
  const fetchReviewDataFromApi = async () => {
    const base_0 = process.env.NEXT_PUBLIC_BASE_URL;
    const url = isPipelineReview ? `${base_0}/pipeline-drugs/${encodeURIComponent(params?.slug || "")}` : `${base_0}/products/public-search-product-by-uid/${encodeURIComponent(params?.productID || "")}/${encodeURIComponent(params?.type || "")}`;
    const res = await fetch(url, {
      cache: "no-store",
      headers: {
        "Content-Type": "application/json"
      }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    return isPipelineReview ? body?.data?.reviews : body?.product?.reviews;
  };
  const reconcileWithApi = async savedId_0 => {
    try {
      const fresh = await fetchReviewDataFromApi();
      if (!Array.isArray(fresh?.reviewsList)) return;
      // Only replace the optimistic copy with data that actually contains
      // the new review; never regress to a list without it.
      const listed = !savedId_0 || fresh.reviewsList.some(r_1 => String(r_1?._id) === String(savedId_0));
      if (listed) setFreshReviewData(fresh);
    } catch (e) {
      console.error("[reviews] could not read back from API; keeping optimistic copy", e);
    }
  };

  // Purge this page's cache entry and get the page re-rendered with the
  // review included, in one round trip. This is a Server Action on purpose:
  // Next awaits the purge before that re-render (a Route Handler such as
  // /api/revalidate purges in the background after replying, which is the
  // race that made saved reviews "vanish"). Any later reload — including
  // the modal's "Back to product" — then reads an already-purged cache.
  const refreshPageAfterReview = async savedId_1 => {
    try {
      const result = isPipelineReview ? await refreshPipelineReviews({
        slug: params?.slug
      }) : await refreshProductReviews({
        type: params?.type,
        productID: params?.productID
      });
      if (result?.ok) return;
      console.warn("[reviews] page cache not refreshed:", result?.reason);
    } catch (e_0) {
      console.error("[reviews] page cache refresh failed:", e_0);
    }
    // Fallback: swap the optimistic copy for the API's record and ask the
    // router for whatever the server has; the override above keeps the
    // review visible even if that render is still stale.
    await reconcileWithApi(savedId_1);
    router.refresh();
  };
  const submitReview = async (ratings_0, review_1, finalReviewData) => {
    const isValid = validateRatingsAndReview(ratings_0, review_1);
    console.log("REVIEW_listing", isValid, ratings_0, review_1, finalReviewData);
    if (!isValid) {
      throw new Error("Please complete all rating fields before submitting.");
    }
    setLoading(true);
    setError("");
    try {
      // Read IP from localStorage (set by IpFetcher in root layout)
      const userIp = localStorage.getItem('user_ip') || '';

      // Prepare review data.
      // FDA products → send `product: <ObjectId>` (backend looks up by _id).
      // Pipeline drugs → send `pipelineSlug: <slug>` (backend resolves slug → _id).
      // The popup populates `finalReviewData.pipelineSlug` from useParams().slug
      // on pipeline routes; we forward it here.
      const targetKey = isPipelineReview ? {
        pipelineSlug: finalReviewData?.pipelineSlug
      } : {
        product
      };
      const data = {
        user: user._id,
        ...ratings_0.reduce((acc, curr) => {
          acc[curr.tag] = curr.count;
          return acc;
        }, {}),
        ...targetKey,
        disclaimerAccepted: true,
        ip: userIp,
        review: review_1,
        // Include additional data from finalReviewData if provided
        ...(finalReviewData && {
          indication: finalReviewData.indication,
          accessibilityScore: finalReviewData.accessibilityScore,
          accessibilityCategory: finalReviewData.accessibilityCategory,
          confidenceScore: finalReviewData.confidenceScore,
          confidenceCategory: finalReviewData.confidenceCategory,
          competitorComparison: finalReviewData.competitorComparison,
          selectedCompetitor: finalReviewData.selectedCompetitor,
          preference: finalReviewData.preference,
          accessibility: finalReviewData.accessibility,
          insightFactors: finalReviewData.insightFactors,
          otherFactorText: finalReviewData.otherFactorText
        })
      };
      console.log("REVIEW_listing data:", data);

      // Get JWT token from cookies.
      // IMPORTANT: must use `.trim().startsWith("token=")` rather
      // than `.includes("token=")` — the substring check would also
      // match `refresh_token=`, `g_csrf_token=`, `xsrf_token=`, etc.
      // When such a cookie sits after `token=` in document.cookie,
      // the forEach overwrites the real JWT with garbage and the
      // BE rejects with "Auth failed". Using `startsWith` after
      // trimming whitespace pins the match to the exact cookie name.
      const cookies = document.cookie.split(";");
      let jwtCookie = null;
      cookies.forEach(cookie => {
        const c = cookie.trim();
        if (c.startsWith("token=")) {
          jwtCookie = "Bearer " + c.slice("token=".length);
        }
      });

      // Submit review to the correct endpoint based on context.
      // /products/* → /product-review/create (writes targetType="product")
      // /pipeline/* → /pipeline-review/create (writes targetType="pipeline")
      const endpoint = isPipelineReview ? `${process.env.NEXT_PUBLIC_BASE_URL}/pipeline-review/create` : `${process.env.NEXT_PUBLIC_BASE_URL}/product-review/create`;
      const res_0 = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${jwtCookie}`
        },
        body: JSON.stringify(data)
      });
      const response = await res_0.json();
      if (res_0.status === 200) {
        setLoading(false);

        // 1. Show the review now, from what was just submitted. This does
        //    not depend on any cache, so it cannot be missing.
        const optimistic = buildOptimisticReview(data, response?.review);
        savedReviewIdRef.current = optimistic._id;
        setFreshReviewData(prev => withReviewPrepended(prev ?? reviewData, optimistic));

        // 2. Purge the cached page and re-render it with the real record
        //    (hero rating, counts, this list). Not awaited: the modal's
        //    success screen must not wait on the product API.
        void refreshPageAfterReview(optimistic._id);
        return response;
      } else {
        setLoading(false);
        // Translate JWT middleware errors (401 with "Auth failed" /
        // "Authorization header missing") into a friendly,
        // action-oriented message. The raw "Auth failed" string was
        // confusing doctors mid-flow — they didn't know they'd been
        // silently logged out. Cover both 401 status AND the raw
        // message in case the BE returns the message with a
        // different status.
        const isAuthError = res_0.status === 401 || response?.message === "Auth failed" || response?.message === "Authorization header missing";

        // Privacy-safe diagnostic footer attached to auth errors so
        // the doctor's screenshot tells us everything we need to
        // reproduce. We include:
        //   - HTTP status from the BE
        //   - cookie NAMES only (never values) so a `refresh_token`
        //     / `g_csrf_token` / `xsrf_token` etc. shows up by name
        //   - the first 8 chars of whatever token was sent so we
        //     can distinguish a real-looking JWT (eyJhbG…) from
        //     garbage (the cookie-poison bug substitutes a non-JWT
        //     here). 8 chars is too short to be useful to a
        //     stealer but enough to spot a malformed token.
        // All three pieces are non-sensitive. The actual JWT body
        // and BE error stack stay server-side.
        const cookieNames = document.cookie.split(";").map(c_0 => c_0.trim().split("=")[0]).filter(Boolean).join(", ");
        const tokenPrefix = jwtCookie ? jwtCookie.replace(/^Bearer\s+/, "").slice(0, 8) + "…" : "(none)";
        const diag = `[diag: HTTP ${res_0.status} • cookies: ${cookieNames || "(none)"} • token sent: ${tokenPrefix}]`;
        const errorMessage_0 = isAuthError ? `Your session has expired. Please log out and log back in, then try again. ${diag}` : `${response.message || "Failed to submit review"} ${diag}`;
        setError(errorMessage_0);
        throw new Error(errorMessage_0);
      }
    } catch (err) {
      console.error("REVIEW_listing error:", err);
      const errorMessage = err.message || "An error occurred while submitting the review";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err; // Re-throw to be caught by the caller
    } finally {
      setLoading(false);
    }
  };
  const [isLoginPopUpOpen, setLoginPopup] = useState(false);
  const handelOpenLoginPopup = () => {
    setLoginPopup(true);
  };
  const handelCloseLoginPopup = () => {
    setLoginPopup(false);
  };

  // Calculate average ratings for each category
  const getCategoryAverage = category => {
    switch (category) {
      case "efficacy":
        return globalEfficacy;
      case "safety":
        return globalSafety;
      case "tolerability":
        return globalTolerability;
      case "adherence":
        return globalAdherence;
      case "priceandaffordability":
        return globalPriceAndAffordability;
      default:
        return 5;
    }
  };

  // Format number with commas
  const formatReviewCount = num => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Calculate star display
  const renderStars = () => {
    const numRating = Number(globalAvg) || 0;
    const clampedRating = Math.min(Math.max(numRating, 0), 5);
    const fullStars = Math.floor(clampedRating);
    const decimal = clampedRating % 1;
    const hasHalfStar = decimal >= 0.5 && clampedRating < 5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    const stars = [];
    for (let i = 0; i < fullStars; i++) {
      stars.push(<RatingStar key={`full-${i}`} className="w-4 h-4" fill="#1E5EFF" clipId={`review_listing_star_full_${i}`} />);
    }
    if (hasHalfStar) {
      stars.push(<div key="half" className="relative w-4 h-4">
					<div className="absolute inset-0">
						<OutlineStar className="w-4 h-4 text-gray-300" />
					</div>
					<div className="absolute inset-0 overflow-hidden" style={{
          width: '50%'
        }}>
						<RatingStar className="w-4 h-4" fill="#1E5EFF" clipId="review_listing_star_half" />
					</div>
				</div>);
    }
    for (let i_0 = 0; i_0 < emptyStars; i_0++) {
      stars.push(<OutlineStar key={`empty-${i_0}`} className="w-4 h-4 text-gray-300" />);
    }
    return stars;
  };

  // State for tracking visibility to trigger animations only once
  const [isConfidenceVisible, setIsConfidenceVisible] = useState(false);
  const [isAccessibilityVisible, setIsAccessibilityVisible] = useState(false);
  const [hasRatingsBeenVisible, setHasRatingsBeenVisible] = useState(false);
  const [hasConfidenceBeenVisible, setHasConfidenceBeenVisible] = useState(false);
  const [hasAccessibilityBeenVisible, setHasAccessibilityBeenVisible] = useState(false);

  // Reset visibility states when collapsing to ensure fresh animations on next expand
  useEffect(() => {
    if (!isAssessmentsExpanded) {
      setIsRatingsVisible(false);
      setIsConfidenceVisible(false);
      setIsAccessibilityVisible(false);
      setHasRatingsBeenVisible(false);
      setHasConfidenceBeenVisible(false);
      setHasAccessibilityBeenVisible(false);
    }
  }, [isAssessmentsExpanded]);

  // Intersection Observer to detect when ratings section comes into view
  useEffect(() => {
    if (!ratingsSectionRef.current || !isAssessmentsExpanded) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && isAssessmentsExpanded && !hasRatingsBeenVisible) {
          setIsRatingsVisible(true);
          setHasRatingsBeenVisible(true);
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px'
    });
    observer.observe(ratingsSectionRef.current);
    return () => {
      if (ratingsSectionRef.current) {
        observer.unobserve(ratingsSectionRef.current);
      }
    };
  }, [isAssessmentsExpanded, hasRatingsBeenVisible]);
  useEffect(() => {
    if (!confidenceRef.current || !isAssessmentsExpanded) return;
    const observer_0 = new IntersectionObserver(entries_0 => {
      entries_0.forEach(entry_0 => {
        if (entry_0.isIntersecting && isAssessmentsExpanded && !hasConfidenceBeenVisible) {
          setIsConfidenceVisible(true);
          setHasConfidenceBeenVisible(true);
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px'
    });
    observer_0.observe(confidenceRef.current);
    return () => {
      if (confidenceRef.current) {
        observer_0.unobserve(confidenceRef.current);
      }
    };
  }, [isAssessmentsExpanded, hasConfidenceBeenVisible]);
  useEffect(() => {
    if (!accessibilityRef.current || !isAssessmentsExpanded) return;
    const observer_1 = new IntersectionObserver(entries_1 => {
      entries_1.forEach(entry_1 => {
        if (entry_1.isIntersecting && isAssessmentsExpanded && !hasAccessibilityBeenVisible) {
          setIsAccessibilityVisible(true);
          setHasAccessibilityBeenVisible(true);
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px'
    });
    observer_1.observe(accessibilityRef.current);
    return () => {
      if (accessibilityRef.current) {
        observer_1.unobserve(accessibilityRef.current);
      }
    };
  }, [isAssessmentsExpanded, hasAccessibilityBeenVisible]);
  return <>
			<div>
				{reviews.length > 0 && <div className="flex justify-between items-center w-full my-4 sm:my-6">
						<div className="text-black text-sm sm:text-[16px] font-medium font-poppins">
							Reviews
						</div>
						{hasPermissionToAddReview(user?.type) && <div onClick={() => dispatch(setReviewPopupOpen(true))} className="text-xs sm:text-[14px] text-[#354EF0] cursor-pointer font-medium font-poppins">
								Write an anonymous review
							</div>}
					</div>}

				{reviews.length !== 0 && <div className="bg-gray-50 rounded-xl sm:rounded-2xl overflow-hidden mb-4 sm:mb-6 p-4 sm:p-6">
						{/* Top Section - Product Name and Rating */}
						<div className="flex flex-row items-start justify-between gap-2 sm:gap-4 mb-4 sm:mb-6">
							{/* Product Name */}
							<div className="flex-1 min-w-0">
								<h1 className="text-sm sm:text-2xl font-bold text-gray-900 font-poppins mb-0.5 sm:mb-1 uppercase">
									{productName || "Product"}
								</h1>
								<p className="text-gray-500 text-[10px] sm:text-sm font-poppins">
									{genericName || "Generic name"}
								</p>
							</div>

							{/* Rating - Simple on mobile, boxed on desktop */}
							<div className="flex items-center gap-1 sm:gap-3 flex-shrink-0 sm:bg-white sm:rounded-xl sm:border sm:border-gray-200 sm:px-5 sm:py-3">
								<span className="text-sm sm:text-3xl font-bold text-gray-900 font-poppins">
									{Number(globalAvg || 0).toFixed(1)}
								</span>
								{/* Single star on mobile */}
								<RatingStar className="w-3.5 h-3.5 sm:hidden" fill="#1E5EFF" clipId="review_listing_star_mobile" />
								{/* Review count on mobile - inline */}
								<span className="text-xs sm:hidden font-medium font-poppins text-gray-400">
									({formatReviewCount(reviews.length)})
								</span>
								{/* Full stars and count on desktop */}
								<div className="hidden sm:flex flex-col items-end mt-1">
									<div className="flex items-center gap-0.5">
										{renderStars()}
									</div>
									{reviews.length > 0 && <span className="text-sm font-medium font-poppins text-gray-400 mt-0.5">
											({formatReviewCount(reviews.length)} {reviews.length === 1 ? 'review' : 'reviews'})
										</span>}
								</div>
							</div>
						</div>

						{/* Metric Cards Row */}
						<div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
							{/* Confidence Card */}
							<MetricDisplayCard icon={() => <BicepsFlexed className="w-5 h-5 text-gray-400" />} iconBgColor="" label="CONFIDENCE" value={globalConfidenceScore} valueSuffix="%" valueColor="#8b5cf6" description="Prescriber confidence in clinical efficacy" tooltipText="Average physician confidence rating on this product's ability to achieve significant clinical outcomes in real-world practice." showSlider={true} sliderType="confidence" />

							{/* Accessibility Card */}
							<MetricDisplayCard icon={() => <LockKeyholeOpen className="w-5 h-5 text-gray-400" />} iconBgColor="" label="ACCESSIBILITY" value={globalAccessibility} valueSuffix="%" valueColor="#3b82f6" description="Ease of access (insurance, prior auth, etc.)" tooltipText="Average physician rating on product accessibility for patients, including insurance coverage, prior authorization ease, and supply chain availability." showSlider={true} sliderType="accessibility" />

							{/* Concerns Card */}
							<MetricDisplayCard icon={() => <AlertTriangle className="w-5 h-5 text-gray-400" />} iconBgColor="" label="CONCERNS" value={ratingCategories.filter(category_0 => {
            const rating_0 = getCategoryAverage(category_0.tag);
            return rating_0 > 0 && rating_0 <= 2;
          }).length} valueSuffix="" valueColor={ratingCategories.filter(category_1 => {
            const rating_1 = getCategoryAverage(category_1.tag);
            return rating_1 > 0 && rating_1 <= 2;
          }).length === 0 ? "#9da2ae" : "#f59e0b"} description={(() => {
            const lowRatedCategories = ratingCategories.filter(category_2 => {
              const rating_2 = getCategoryAverage(category_2.tag);
              return rating_2 > 0 && rating_2 <= 2;
            });
            if (lowRatedCategories.length === 0) return "No concerns reported";
            return `Reported ${lowRatedCategories.map(c_1 => c_1.label.toLowerCase()).join(" and ")} issues`;
          })()} tooltipText="Number of categories where physicians have reported low ratings, indicating potential areas of concern." showSlider={false} inverseColor={true} maxValue={5} />
						</div>
					</div>}

				{/* Expandable Arrow Button - Absolute positioned at bottom center */}
				{/* {!isAssessmentsExpanded && (
     			<button
     				onClick={(e) => {
     					e.stopPropagation();
     					setIsAssessmentsExpanded(true);
     				}}
     				className="absolute bottom-8 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-8 h-8 rounded-full bg-white border flex items-center justify-center hover:bg-gray-100 transition-colors shadow-sm z-10"
     				aria-label="Expand assessments"
     			>
     				<FiChevronDown className="w-4 h-4" />
     			</button>
     		)} */}
				{/* </div> */}

				{/* Expandable Content */}
				{/* <div
     		className={`transition-all duration-500 ease-in-out overflow-hidden ${isAssessmentsExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
     	>
     		//Performance Ratings 
     		<div ref={ratingsSectionRef} className="px-8 py-6 border-t border-gray-200 bg-gray-50">
     			<div className="flex items-center gap-3 mb-5">
     				<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
     					<FiActivity className="w-4 h-4 text-primary-primaryBlue" />
     				</div>
     				<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">CLINICAL PERFORMANCE</div>
     			</div>
     			<div className="grid grid-cols-2 gap-4 mb-8">
     				{ratingCategories.map((category) => {
     					const rating = getCategoryAverage(category.tag);
     					const roundedRating = Math.round(rating);
     					const peerAvg = peerAverages[category.tag] || 3.5;
     					const peerStatus = getPeerComparisonStatus(rating, peerAvg);
     					const bgColor = getRatingColor(roundedRating);
     						return (
     						<div key={category.tag} className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg bg-white">
     							<RatingCircle
     								rating={rating}
     								roundedRating={rating}
     								bgColor={bgColor}
     								label=""
     								isVisible={isRatingsVisible && isAssessmentsExpanded}
     							/>
     							<div className="flex-1">
     								<div className="text-xs font-semibold text-gray-900 font-poppins mb-1">{category.label}</div>
     								<div className="text-xs text-gray-500 mb-1">Rating: {rating.toFixed(1)}/5.0</div>
     								<div className="flex gap-2 items-center">
     									<div className="text-xs text-gray-400">Peer avg: {peerAvg.toFixed(1)}/5.0</div>
     									<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${peerStatus.bgColor} ${peerStatus.color} border ${peerStatus.borderColor}`}>
     										{peerStatus.label}
     									</span>
     								</div>
     							</div>
     						</div>
     					);
     				})}
     			</div>
     				// Clinical Concerns 
     			{(() => {
     				const lowRatings = ratingCategories.filter(category => {
     					const rating = getCategoryAverage(category.tag);
     					return rating > CLINICAL_CONCERN.MIN && rating <= CLINICAL_CONCERN.MAX;
     				});
     					if (lowRatings.length === 0) return null;
     					return (
     					<div className="pt-3">
     						<div className="text-sm uppercase tracking-wider text-yellow-700 font-bold mb-4 flex items-center gap-2">
     							<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
     								<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
     							</svg>
     							CLINICAL CONCERNS
     						</div>
     						<div className="flex flex-wrap gap-3 mb-4">
     							{lowRatings.map((category) => {
     								return (
     									<div
     										key={category.tag}
     										className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 border border-yellow-300 rounded-lg"
     									>
     										<span className="text-sm font-semibold text-yellow-800 font-poppins">
     											{category.label}
     										</span>
     										<span className="text-xs text-yellow-700 font-poppins">
     											(Low)
     										</span>
     									</div>
     								);
     							})}
     						</div>
     						<p className="text-sm text-gray-700 font-poppins leading-relaxed">
     							These categories have lower average ratings that may impact patient outcomes.
     						</p>
     					</div>
     				);
     			})()}
     		</div>
     			// Accessibility Assessment 
     		<div ref={accessibilityRef} className="px-8 py-8 border-t border-gray-200 bg-gray-50">
     			<div className="flex items-center gap-3 mb-5">
     				<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
     					<FiPackage className="w-4 h-4 text-primary-primaryBlue" />
     				</div>
     				<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">ACCESSIBILITY</div>
     			</div>
     			<div className="flex items-center gap-8">
     				<div className="flex-1 flex justify-center">
     					<div className="flex flex-col items-center gap-3">
     						<AccessibilityCircle
     							score={globalAccessibility}
     							shouldAnimate={isAccessibilityVisible}
     						/>
     						<div className="text-sm text-gray-600 font-poppins text-center">
     							{getAccessibilityLevel(globalAccessibility).label}
     						</div>
     						<div className="flex flex-col items-center gap-1">
     							<div className="text-xs text-gray-400">Peer avg: {peerAccessibilityAvg}%</div>
     							{(() => {
     								const peerStatus = getPeerComparisonStatusPercent(globalAccessibility, peerAccessibilityAvg);
     								return (
     									<span className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${peerStatus.bgColor} ${peerStatus.color} border ${peerStatus.borderColor}`}>
     										{peerStatus.label}
     									</span>
     								);
     							})()}
     						</div>
     					</div>
     				</div>
     				<div className="flex-1 flex justify-center font-normal">
     					<AccessibilityLevelIndicator currentScore={globalAccessibility} />
     				</div>
     			</div>
     		</div>
     			// Clinical Confidence Assessment //
     		<div ref={confidenceRef} className="px-8 py-8 border-t border-gray-200 bg-gray-50">
     			<div className="flex items-center gap-3 mb-5">
     				<div className="w-8 h-8 rounded-full bg-primary-primaryBlue/10 flex items-center justify-center flex-shrink-0">
     					<FiShield className="w-4 h-4 text-primary-primaryBlue" />
     				</div>
     				<div className="text-sm uppercase tracking-wider text-gray-700 font-bold">CLINICAL CONFIDENCE</div>
     			</div>
     			<div className="flex items-center gap-8">
     				<div className="flex-1 flex justify-center">
     					<div className="flex flex-col items-center gap-3">
     						<ConfidenceCircle
     							score={globalConfidenceScore}
     							shouldAnimate={isConfidenceVisible}
     						/>
     						<div className="text-sm text-gray-600 font-poppins text-center">
     							{getConfidenceLevel(globalConfidenceScore).label}
     						</div>
     						<div className="flex flex-col items-center gap-1">
     							<div className="text-xs text-gray-400">Peer avg: {peerConfidenceAvg}%</div>
     							{(() => {
     								const peerStatus = getPeerComparisonStatusPercent(globalConfidenceScore, peerConfidenceAvg);
     								return (
     									<span className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${peerStatus.bgColor} ${peerStatus.color} border ${peerStatus.borderColor}`}>
     										{peerStatus.label}
     									</span>
     								);
     							})()}
     						</div>
     					</div>
     				</div>
     				<div className="flex-1 flex justify-center">
     					<ConfidenceLevelIndicator currentScore={globalConfidenceScore} />
     				</div>
     			</div>
     		</div>
     			// Collapse Arrow Button at Bottom 
     		<div className="flex justify-center pb-5">
     			<button
     				onClick={() => setIsAssessmentsExpanded(false)}
     				className="w-8 h-8 rounded-full bg-white border flex items-center justify-center hover:bg-gray-100 transition-colors shadow-sm"
     				aria-label="Collapse assessments"
     			>
     				<FiChevronDown className="w-4 h-4 transform rotate-180" />
     			</button>
     		</div>
     	</div> */}
				{/* </div> */}
				{/* // )} */}

				{reviews.length == 0 && isUserLoggedIn && <div className="mt-4 sm:mt-6 w-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm mb-4 sm:mb-6">
						{/* Content Section */}
						<div className="px-4 sm:px-6 py-4 sm:py-6 bg-white">
							<div className="flex flex-col items-center text-center mb-4 sm:mb-6">
								<div className="mb-3 sm:mb-4">
									<NoReviewIcon />
								</div>
								<h4 className="text-lg sm:text-xl font-bold text-gray-900 font-poppins mb-1.5 sm:mb-2">
									{user?.type === "reps" ? "No Reviews Yet" : "Be the First to Review"}
								</h4>
								<p className="text-xs sm:text-sm text-gray-600 font-poppins mb-4 sm:mb-6 max-w-md">
									{user?.type === "reps" ? <>No reviews have been posted for <span className="font-semibold text-gray-900">{productName || 'this product'}</span> yet. Check back later for physician insights.</> : <>Share your experience with <span className="font-semibold text-gray-900">{productName || 'this product'}</span> and help peers make informed prescribing decisions.</>}
								</p>
								{user?.type !== "reps" && <p className="text-xs sm:text-sm text-gray-600 font-poppins mb-1">
										Your <span className="font-medium text-primary-primaryBlue">45-second review</span> helps thousands of colleagues.
									</p>}
							</div>

							{/* Benefits List + Button - hidden for reps */}
						{user?.type !== "reps" && <>
							<div className="max-w-2xl mx-auto mb-4 sm:mb-6 space-y-2">
								<div className="flex items-start gap-2.5 sm:gap-3">
									<div className="flex-shrink-0 w-4 sm:w-5 h-4 sm:h-5 rounded-full flex items-center justify-center mt-0.5" style={{
                  backgroundColor: '#E0E7FF'
                }}>
										<FiCheck className="w-3 sm:w-3.5 h-3 sm:h-3.5" style={{
                    color: '#1E5EFF'
                  }} />
									</div>
									<div className="flex-1 text-left">
										<h5 className="text-xs sm:text-sm font-semibold text-gray-900 font-poppins mb-0.5">
											Help Peers Make Better Decisions
										</h5>
										<p className="text-[11px] sm:text-xs text-gray-600 font-poppins">
											Your real-world insights guide colleagues
										</p>
									</div>
								</div>
								<div className="flex items-start gap-2.5 sm:gap-3">
									<div className="flex-shrink-0 w-4 sm:w-5 h-4 sm:h-5 rounded-full flex items-center justify-center mt-0.5" style={{
                  backgroundColor: '#E0E7FF'
                }}>
										<FiCheck className="w-3 sm:w-3.5 h-3 sm:h-3.5" style={{
                    color: '#1E5EFF'
                  }} />
									</div>
									<div className="flex-1 text-left">
										<h5 className="text-xs sm:text-sm font-semibold text-gray-900 font-poppins mb-0.5">
											Anonymous to Pharmaceutical Companies
										</h5>
										<p className="text-[11px] sm:text-xs text-gray-600 font-poppins">
											Your identity stays private only specialty/region shown
										</p>
									</div>
								</div>
								<div className="flex items-start gap-2.5 sm:gap-3">
									<div className="flex-shrink-0 w-4 sm:w-5 h-4 sm:h-5 rounded-full flex items-center justify-center mt-0.5" style={{
                  backgroundColor: '#E0E7FF'
                }}>
										<FiCheck className="w-3 sm:w-3.5 h-3 sm:h-3.5" style={{
                    color: '#1E5EFF'
                  }} />
									</div>
									<div className="flex-1 text-left">
										<h5 className="text-xs sm:text-sm font-semibold text-gray-900 font-poppins mb-0.5">
											See Peer Benchmarks
										</h5>
										<p className="text-[11px] sm:text-xs text-gray-600 font-poppins">
											Compare your experience to specialty peers
										</p>
									</div>
								</div>
							</div>

							{/* Button */}
							<div className="flex justify-center">
								<button onClick={() => dispatch(setReviewPopupOpen(true))} className="w-full max-w-md inline-flex items-center justify-center gap-2 bg-primary-primaryBlue hover:bg-primary-primaryBlue/90 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold font-poppins transition-colors">
									<span>Write First Review</span>
									<svg className="w-3.5 sm:w-4 h-3.5 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
									</svg>
								</button>
						</div>
						</>}
						</div>
					</div>}

				{reviews.length == 0 && !isUserLoggedIn && <div className="w-full justify-center flex-col flex py-12 sm:py-20 px-4 items-center">
						<NoReviewIcon />
						<div className="text-xs sm:text-[14px] font-medium text-chocolateGrey pt-2 sm:pt-3 text-center">
							There isn't any reviews posted yet. You can be the first to review
							the product
						</div>
						<button onClick={handelOpenLoginPopup} className="text-primary-primaryBlue border border-primary-primaryBlue px-3 sm:px-4 mt-4 sm:mt-6 rounded-lg py-2 sm:py-2.5 text-sm sm:text-[16px] font-medium">
							Login to Write a Review
						</button>
					</div>}

				{reviews.map((review_2, index) => {
        if (isHidden && index >= 2) return null;
        return <ReviewCard review={review_2} productName={productName} productImage={productImage} key={index + "_review"} />;
      })}

				{showMore && <div onClick={() => setIsHidden(!isHidden)} className="flex text-sm sm:text-[16px] font-semibold text-[#354EF0] font-poppins pt-3 sm:pt-4 cursor-pointer justify-center">
						{isHidden ? "Show more" : "Show less"}
					</div>}
			</div>

			<div className="z-50 relative">
				{openReviewPopup && isUserLoggedIn && user?.type !== "reps" && <ReviewsDisclaimer error={error} loading={loading} submitReview={submitReview} onClose={() => dispatch(closeReviewPopup())} isVerified={isVerified} productName={productName} productImage={productImage} productID={product?.productAlias || product?._id} />}
			</div>

			{isLoginPopUpOpen && <LoginPopup onClose={handelCloseLoginPopup} />}
		</>;
};
export default ReviewListing;